import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

import { IConfirmPaymentStripeDtoOut, IPaymentIntentDtoIn } from '../../interfaces/stripe.interface';
import { StripePaymentService } from '../../services/stripe-payment.service';
import { ConfirmPayment, CreatePaymentIntent, ResetErrorStripe, ResetStripeState } from '../actions/stripe.action';

export interface StripeStateModel {
    paymentIntent: IPaymentIntentDtoIn;
    paymentError: string;
    showGenericError: boolean;
    isInTransaction: boolean;
}

const defaults: StripeStateModel = {
    paymentIntent: {
        payment_intent_id: null,
        client_secret: null,
    },
    paymentError: null,
    showGenericError: null,
    isInTransaction: false,
};
@State<StripeStateModel>({
    name: 'Stripe',
    defaults,
})
@Injectable()
export class StripeState {
    @Selector()
    public static paymentIntent(state: StripeStateModel): IPaymentIntentDtoIn {
        return state.paymentIntent;
    }

    @Selector()
    public static showGenericError(state: StripeStateModel): boolean {
        return state.showGenericError;
    }

    @Selector()
    public static paymentError(state: StripeStateModel): string {
        return state.paymentError;
    }

    @Selector()
    public static isInTransaction(state: StripeStateModel): boolean {
        return state.isInTransaction;
    }

    constructor(private stripePaymentService: StripePaymentService) {}

    @Action(CreatePaymentIntent)
    public createPaymentIntent(ctx: StateContext<StripeStateModel>, action: CreatePaymentIntent): Observable<IPaymentIntentDtoIn> {
        return this.stripePaymentService.createPaymentIntent(action.paymentIntentPayload).pipe(
            tap((result: IPaymentIntentDtoIn) => {
                ctx.patchState({
                    paymentIntent: result,
                });
            }),
            catchError((err: any) => {
                ctx.patchState({
                    showGenericError: true,
                });

                return throwError(err);
            })
        );
    }

    @Action(ConfirmPayment)
    public confirmPayment(ctx: StateContext<StripeStateModel>, action: ConfirmPayment): Observable<IConfirmPaymentStripeDtoOut> {
        const state: StripeStateModel = ctx.getState();
        ctx.patchState({ isInTransaction: true });
        return action.stripe.confirmCardPayment(state.paymentIntent.client_secret, action.data).pipe(
            tap((result: IConfirmPaymentStripeDtoOut) => {
                if (result.error) {
                    ctx.patchState({
                        paymentError: result.error.message,
                    });
                } else {
                    ctx.patchState({
                        paymentError: null,
                    });
                }
            }),
            finalize(() => {
                ctx.patchState({ isInTransaction: false });
            })
        );
    }

    @Action(ResetErrorStripe)
    public resetErrorStripe(ctx: StateContext<StripeStateModel>): void {
        ctx.patchState({
            showGenericError: false,
        });
    }

    @Action(ResetStripeState)
    public resetStripeState(ctx: StateContext<StripeStateModel>): void {
        ctx.setState(defaults);
    }
}
